// The room plan, drawn from the same numbers that are sent: corners from the solver, items
// placed by their offsets along their wall. Units in the SVG are millimetres, y flipped so
// north is up.

import { cn } from "@artisan/ui";

import { cornerLabel, type RoomDraft, wallLabel } from "../project/model";
import { solveRoom } from "../survey/solve";
import { umToMmText } from "../survey/units";
import { nth } from "../lib/at";

export interface RoomGeometry {
  /** corner positions in mm, plan frame */
  pts: [number, number][];
  solved: boolean;
}

export function roomGeometry(room: RoomDraft): RoomGeometry {
  const r = solveRoom({
    sketch: room.sketch,
    walls: room.walls.map((w) => w?.um ?? null),
    diagonals: room.diagonals.map((d) => ({ a: d.a, b: d.b, um: d.length?.um ?? null })),
  });
  if (r.ok) return { pts: r.at.map(([x, y]) => [x / 1000, y / 1000]), solved: true };
  return { pts: room.sketch.map(([x, y]) => [x * 1000, y * 1000]), solved: false };
}

/** A point along wall i, `along` mm from its start, `inward` mm into the room. */
export function onWall(g: RoomGeometry, i: number, along: number, inward = 0): [number, number] {
  const n = g.pts.length;
  const a = nth(g.pts, i);
  const b = nth(g.pts, (i + 1) % n);
  const len = Math.hypot(b[0] - a[0], b[1] - a[1]) || 1;
  const ux = (b[0] - a[0]) / len;
  const uy = (b[1] - a[1]) / len;
  // Clockwise outline: the inside is to the right of the wall's direction.
  return [a[0] + ux * along + uy * inward, a[1] + uy * along - ux * inward];
}

function wallLen(g: RoomGeometry, i: number): number {
  const a = nth(g.pts, i);
  const b = nth(g.pts, (i + 1) % g.pts.length);
  return Math.hypot(b[0] - a[0], b[1] - a[1]);
}

const pt = ([x, y]: [number, number]) => `${x.toFixed(1)},${(-y).toFixed(1)}`;

export interface PlanViewProps {
  room: RoomDraft;
  selectedWall?: number | null;
  onSelectWall?: (i: number) => void;
  /** diagonals to show as "measure this" before they are measured */
  planned?: [number, number][];
  compact?: boolean;
  className?: string;
  label?: string;
}

export function PlanView({ room, selectedWall, onSelectWall, planned = [], compact, className, label }: PlanViewProps) {
  const g = roomGeometry(room);
  const n = g.pts.length;
  const xs = g.pts.map((p) => p[0]);
  const ys = g.pts.map((p) => p[1]);
  const w = Math.max(...xs) - Math.min(...xs) || 1000;
  const h = Math.max(...ys) - Math.min(...ys) || 1000;
  const size = Math.max(w, h);
  const pad = size * (compact ? 0.06 : 0.2);
  const k = size / 40; // one "unit" of line and text size
  const vb = `${Math.min(...xs) - pad} ${-Math.max(...ys) - pad} ${w + 2 * pad} ${h + 2 * pad}`;
  const measuredDiag = new Set(room.diagonals.filter((d) => d.length).map((d) => `${d.a}-${d.b}`));

  return (
    <svg viewBox={vb} className={cn("block select-none", className)} role="img" aria-label={label ?? `${room.name} өрөөний план`}>
      <defs>
        <pattern id={`hatch-${room.id}`} width={k * 0.8} height={k * 0.8} patternUnits="userSpaceOnUse" patternTransform="rotate(45)">
          <line x1="0" y1="0" x2="0" y2={k * 0.8} stroke="var(--muted-foreground)" strokeWidth={k * 0.12} />
        </pattern>
      </defs>
      <polygon points={g.pts.map(pt).join(" ")} fill="var(--card)" stroke="none" />
      {/* zones */}
      {!compact &&
        room.zones.map((z) => {
          const len = Math.min(z.length / 1000, wallLen(g, z.wall));
          const d = z.depth / 1000;
          const o = z.offset / 1000;
          const corners = [onWall(g, z.wall, o, 0), onWall(g, z.wall, o + len, 0), onWall(g, z.wall, o + len, d), onWall(g, z.wall, o, d)];
          const fill = z.kind === "wall" ? "color-mix(in oklab, var(--oak) 18%, transparent)" : "color-mix(in oklab, var(--oak) 42%, var(--card))";
          return (
            <g key={z.id}>
              <polygon
                points={corners.map(pt).join(" ")}
                fill={fill}
                stroke="var(--oak)"
                strokeWidth={k * 0.12}
                strokeDasharray={z.kind === "wall" ? `${k * 0.5} ${k * 0.3}` : undefined}
              />
              {z.partitions.slice(1).map((p) => (
                <line
                  key={p.id}
                  x1={onWall(g, z.wall, o + p.offset / 1000, 0)[0]}
                  y1={-onWall(g, z.wall, o + p.offset / 1000, 0)[1]}
                  x2={onWall(g, z.wall, o + p.offset / 1000, d)[0]}
                  y2={-onWall(g, z.wall, o + p.offset / 1000, d)[1]}
                  stroke="var(--oak)"
                  strokeWidth={k * 0.1}
                />
              ))}
            </g>
          );
        })}
      {/* obstructions */}
      {!compact &&
        room.obstructions.map((o) => {
          if (!o.offset || !o.width || !o.depth) return null;
          const a = o.offset.um / 1000;
          const b = a + o.width.um / 1000;
          const d = o.depth.um / 1000;
          const c = [onWall(g, o.wall, a, 0), onWall(g, o.wall, b, 0), onWall(g, o.wall, b, d), onWall(g, o.wall, a, d)];
          return <polygon key={o.id} points={c.map(pt).join(" ")} fill={`url(#hatch-${room.id})`} stroke="var(--muted-foreground)" strokeWidth={k * 0.1} />;
        })}
      {/* diagonals */}
      {room.diagonals.map((d) => {
        const a = nth(g.pts, d.a);
        const b = nth(g.pts, d.b);
        const mid: [number, number] = [(a[0] + b[0]) / 2, (a[1] + b[1]) / 2];
        return (
          <g key={`${d.a}-${d.b}`}>
            <line
              x1={a[0]}
              y1={-a[1]}
              x2={b[0]}
              y2={-b[1]}
              stroke={d.length ? "var(--muted-foreground)" : "var(--primary)"}
              strokeWidth={k * 0.09}
              strokeDasharray={`${k * 0.6} ${k * 0.4}`}
            />
            {!compact && d.length ? (
              <text x={mid[0]} y={-mid[1]} fontSize={k * 0.95} textAnchor="middle" dy={-k * 0.4} fill="var(--muted-foreground)" fontFamily="var(--font-mono)">
                {umToMmText(d.length.um)}
              </text>
            ) : null}
          </g>
        );
      })}
      {planned
        .filter(([a, b]) => !measuredDiag.has(`${a}-${b}`) && !room.diagonals.some((d) => d.a === a && d.b === b))
        .map(([a, b]) => (
          <line
            key={`p${a}-${b}`}
            x1={nth(g.pts, a)[0]}
            y1={-nth(g.pts, a)[1]}
            x2={nth(g.pts, b)[0]}
            y2={-nth(g.pts, b)[1]}
            stroke="var(--primary)"
            strokeOpacity={0.55}
            strokeWidth={k * 0.09}
            strokeDasharray={`${k * 0.6} ${k * 0.4}`}
          />
        ))}
      {/* walls */}
      {g.pts.map((a, i) => {
        const b = nth(g.pts, (i + 1) % n);
        const selected = selectedWall === i;
        const wallM = room.walls[i] ?? null;
        const measured = Boolean(wallM);
        // label outside the room: opposite the inward normal
        const out = onWall(g, i, wallLen(g, i) / 2, -k * (compact ? 1.6 : 2.2));
        const dimPos = onWall(g, i, wallLen(g, i) / 2, k * 1.9);
        const angle = (Math.atan2(-(b[1] - a[1]), b[0] - a[0]) * 180) / Math.PI;
        const upright = angle > 90 || angle < -90 ? angle + 180 : angle;
        return (
          <g
            key={i}
            onClick={onSelectWall ? () => onSelectWall(i) : undefined}
            className={onSelectWall ? "cursor-pointer" : undefined}
            role={onSelectWall ? "button" : undefined}
            aria-label={onSelectWall ? `${wallLabel(i)} хана` : undefined}
          >
            {onSelectWall ? <line x1={a[0]} y1={-a[1]} x2={b[0]} y2={-b[1]} stroke="transparent" strokeWidth={k * 3} /> : null}
            <line
              x1={a[0]}
              y1={-a[1]}
              x2={b[0]}
              y2={-b[1]}
              stroke={selected ? "var(--primary)" : measured ? "var(--foreground)" : "var(--input)"}
              strokeWidth={k * (compact ? 0.5 : 0.42)}
              strokeLinecap="square"
              strokeDasharray={measured || g.solved ? undefined : `${k * 0.8} ${k * 0.5}`}
            />
            {!compact ? (
              <>
                <circle cx={out[0]} cy={-out[1]} r={k * 1.05} fill={selected ? "var(--primary)" : "var(--card)"} stroke={selected ? "var(--primary)" : "var(--border)"} strokeWidth={k * 0.1} />
                <text x={out[0]} y={-out[1]} dy={k * 0.38} fontSize={k * 1.05} fontWeight={600} textAnchor="middle" fill={selected ? "var(--primary-foreground)" : "var(--foreground)"}>
                  {wallLabel(i)}
                </text>
                {wallM ? (
                  <text
                    x={dimPos[0]}
                    y={-dimPos[1]}
                    dy={k * 0.35}
                    fontSize={k * 1.0}
                    textAnchor="middle"
                    fill="var(--foreground)"
                    fontFamily="var(--font-mono)"
                    transform={`rotate(${upright} ${dimPos[0]} ${-dimPos[1]})`}
                  >
                    {umToMmText(wallM.um)}
                  </text>
                ) : null}
              </>
            ) : null}
          </g>
        );
      })}
      {/* openings drawn over the wall line */}
      {room.openings.map((o) => {
        if (!o.offset || !o.width) return null;
        const a0 = o.offset.um / 1000;
        const a1 = a0 + o.width.um / 1000;
        const s = onWall(g, o.wall, a0);
        const e = onWall(g, o.wall, a1);
        return (
          <g key={o.id}>
            <line x1={s[0]} y1={-s[1]} x2={e[0]} y2={-e[1]} stroke="var(--card)" strokeWidth={k * 0.5} />
            {o.kind === "window" ? (
              <>
                <line x1={s[0]} y1={-s[1]} x2={e[0]} y2={-e[1]} stroke="var(--chart-2)" strokeWidth={k * 0.14} />
                <line
                  x1={onWall(g, o.wall, a0, k * 0.3)[0]}
                  y1={-onWall(g, o.wall, a0, k * 0.3)[1]}
                  x2={onWall(g, o.wall, a1, k * 0.3)[0]}
                  y2={-onWall(g, o.wall, a1, k * 0.3)[1]}
                  stroke="var(--chart-2)"
                  strokeWidth={k * 0.1}
                />
              </>
            ) : o.kind === "door" && !compact ? (
              <path
                d={`M ${pt(s)} L ${pt(onWall(g, o.wall, a0, a1 - a0))} A ${a1 - a0} ${a1 - a0} 0 0 0 ${pt(e)}`}
                fill="none"
                stroke="var(--muted-foreground)"
                strokeWidth={k * 0.09}
              />
            ) : null}
          </g>
        );
      })}
      {/* utilities */}
      {!compact &&
        room.utilities.map((u) => {
          if (!u.offset) return null;
          const p = onWall(g, u.wall, u.offset.um / 1000, k * 0.9);
          const color = u.kind.startsWith("water") ? "var(--chart-1)" : u.kind === "drain" ? "var(--chart-5)" : u.kind === "gas" ? "var(--warning)" : "var(--chart-2)";
          return <circle key={u.id} cx={p[0]} cy={-p[1]} r={k * 0.55} fill={color} stroke="var(--card)" strokeWidth={k * 0.12} />;
        })}
      {/* corners */}
      {!compact &&
        g.pts.map((p, i) => {
          // Inside the corner, on its bisector: the walls run clockwise, so the room is on the
          // right of each wall (this also holds at re-entrant corners, unlike the centroid).
          const prev = nth(g.pts, (i - 1 + n) % n);
          const next = nth(g.pts, (i + 1) % n);
          const unit = (x: number, y: number) => {
            const l = Math.hypot(x, y) || 1;
            return [x / l, y / l] as const;
          };
          const [ax, ay] = unit(p[0] - prev[0], p[1] - prev[1]);
          const [bx, by] = unit(next[0] - p[0], next[1] - p[1]);
          const [dx, dy] = unit(ay + by, -ax - bx);
          const q: [number, number] = [p[0] + dx * k * 1.6, p[1] + dy * k * 1.6];
          return (
            <g key={`c${i}`}>
              <circle cx={p[0]} cy={-p[1]} r={k * 0.35} fill="var(--foreground)" />
              <text x={q[0]} y={-q[1]} dy={k * 0.32} fontSize={k * 0.85} textAnchor="middle" fill="var(--muted-foreground)" fontFamily="var(--font-mono)">
                {cornerLabel(i)}
              </text>
            </g>
          );
        })}
    </svg>
  );
}
